import { useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useNotify, useRefresh } from 'react-admin';
import axiosNest from '../api/axiosNest';
import { toErrorMessage } from '../admin/userOffers/orderActionsApi';

/**
 * Wczytanie komunikatu tisys otrzymanego od klienta.
 *
 * ## Dlaczego to dochodzi
 *
 * Parser komunikatów istniał od sierpnia, `upsertFromXml` też — i **nic ich
 * nie wołało**. Nie było drogi, którą komunikat z VIA mógłby wejść do
 * systemu, więc lista zleceń VIA stała nad tabelą, która nie miała jak się
 * zapełnić.
 *
 * ## Dlaczego plik czytamy w przeglądarce
 *
 * Bo to jedno pole tekstowe, a nie załącznik. Wysyłamy samą treść XML-a
 * w ciele żądania, zamiast wciągać obsługę przesyłania wieloczęściowego dla
 * jednego przypadku.
 *
 * ## Co się dzieje po wczytaniu
 *
 * Komunikat trafia do odczytu (W11), a jeśli niesie numer zlecenia, które
 * sami wysłaliśmy — wiąże się z nim i domyka pętlę zapisu. Mówimy wprost,
 * czy dopasowanie nastąpiło: „wczytano, ale nie rozpoznano jako nasze" to
 * inna sytuacja niż „wczytano i powiązano", a operator musi je odróżnić.
 */

interface UploadResult {
  message: { id: number; orderNumber: string | null; transportId: string };
  match: {
    matched: boolean;
    orderNumber?: string;
    state?: string;
    reason?: string;
  } | null;
}

export const MessageUploadPanel = () => {
  const notify = useNotify();
  const refresh = useRefresh();
  const fileInput = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<UploadResult | null>(null);

  const upload = (file: File) =>
    void (async () => {
      setBusy(true);
      try {
        const xml = await file.text();
        const { data } = await axiosNest.post<UploadResult>(
          '/via-orders/load',
          { xml, fileName: file.name },
        );
        setResult(data);
        notify(`Wczytano komunikat ${data.message.transportId}`, {
          type: 'success',
        });
        refresh();
      } catch (e) {
        notify(toErrorMessage(e), { type: 'error' });
      } finally {
        setBusy(false);
        // Bez tego wskazanie TEGO SAMEGO pliku drugi raz nie wywoła
        // zdarzenia — a przy ponawianiu po błędzie to najczęstszy przypadek.
        //
        // ⚠️ Tej linii NIE pokrywa test: jsdom nie odwzorowuje `value` pola
        // plikowego, więc każda asercja na nią przechodzi niezależnie od
        // tego, czy linia istnieje. Sprawdzone celową usterką. Zostawiam bez
        // testu zamiast pisać taki, który nie może paść.
        if (fileInput.current) fileInput.current.value = '';
      }
    })();

  return (
    <Box sx={{ p: 1 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<UploadFileIcon />}
          disabled={busy}
          onClick={() => fileInput.current?.click()}
        >
          Wczytaj komunikat XML
        </Button>
        {busy && <CircularProgress size={18} />}
        <Typography variant="caption" color="text.secondary">
          Plik tisys otrzymany od klienta. Docelowo komunikaty będą
          przychodzić do folderu — jego adresu jeszcze nie mamy.
        </Typography>
      </Stack>

      <input
        ref={fileInput}
        type="file"
        accept=".xml,text/xml,application/xml"
        style={{ display: 'none' }}
        aria-label="Plik komunikatu XML"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) upload(file);
        }}
      />

      {result && (
        <Alert
          severity={result.match?.matched ? 'success' : 'info'}
          sx={{ mt: 1 }}
        >
          {result.match?.matched ? (
            <>
              Powiązano z naszym zleceniem{' '}
              <strong>{result.match.orderNumber}</strong> — stan zapisu:{' '}
              {result.match.state}.
            </>
          ) : (
            <>
              Komunikat wczytany, ale <strong>nie rozpoznano go jako nasz</strong>
              {result.match?.reason ? ` — ${result.match.reason}` : ''}.
              To normalne dla zleceń, których nie wysyłaliśmy.
            </>
          )}
        </Alert>
      )}
    </Box>
  );
};
